import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ImagePlus, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { api, ensureOk, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { leerBase64, validarImagen } from '@/lib/archivos';

import {
  nombreEspecialidad,
  useEspecialidades,
  usePregunta,
  type PreguntaBody,
  type PreguntaDetalle,
  type SugerenciaIa,
} from './queries';

const LETRAS = ['A', 'B', 'C', 'D'];

type Form = Required<Pick<PreguntaBody, 'prompt' | 'options'>> & PreguntaBody;

function desde(p: PreguntaDetalle | null, base: number | null): Form {
  if (!p) {
    return {
      ...(base ? { baseQuestionId: base } : {}),
      clinicalCase: '',
      prompt: '',
      options: ['', '', '', ''],
      explanation: '',
      bibliography: '',
      pivot: '',
      code: '',
    };
  }
  return {
    specialtyId: p.specialtyId,
    clinicalCase: p.clinicalCase ?? '',
    prompt: p.prompt,
    options: [0, 1, 2, 3].map((i) => p.options[i]?.text ?? ''),
    explanation: p.explanation ?? '',
    bibliography: p.bibliography ?? '',
    pivot: p.pivot ?? '',
    code: p.code ?? '',
    isEnglish: p.isEnglish,
    isOnline: p.isOnline,
    isOnlineDemo: p.isOnlineDemo,
    isQuiz: p.isQuiz,
    isQuizDemo: p.isQuizDemo,
  };
}

/** Formulario de una pregunta (base o subsecuente). */
function FormPregunta({
  actual,
  baseId,
  onGuardada,
}: {
  actual: PreguntaDetalle | null;
  baseId: number | null;
  onGuardada: (p: PreguntaDetalle) => void;
}) {
  const editable = useCan('preguntas', 'editar');
  const esp = useEspecialidades();
  const [f, setF] = useState<Form>(() => desde(actual, baseId));
  const [sugerencia, setSugerencia] = useState<SugerenciaIa | null>(null);
  const [indicaciones, setIndicaciones] = useState('');
  const poner = (c: Partial<Form>) => setF((v) => ({ ...v, ...c }));
  const esSubsecuente = baseId !== null || actual?.baseQuestionId != null;
  const solo = !editable;

  const guardar = useMutation({
    mutationFn: () => {
      const body = {
        ...f,
        code: f.code || null,
        explanation: f.explanation || null,
        bibliography: f.bibliography || null,
        pivot: f.pivot || null,
      };
      return actual
        ? unwrap<PreguntaDetalle>(
            api.PATCH('/api/v1/admin/questions/{id}', {
              params: { path: { id: String(actual.id) } },
              body,
            }),
          )
        : unwrap<PreguntaDetalle>(api.POST('/api/v1/admin/questions', { body }));
    },
    onSuccess: (p) => {
      toast.success('Pregunta guardada.');
      onGuardada(p);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const sugerir = useMutation({
    mutationFn: () =>
      unwrap<SugerenciaIa>(
        api.POST('/api/v1/admin/questions/{id}/rewrite', {
          params: { path: { id: String(actual?.id) } },
          body: { instructions: indicaciones },
        }),
      ),
    onSuccess: setSugerencia,
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const aplicar = (s: SugerenciaIa) => {
    const correcta = s.options.find((o) => o.isCorrect);
    const otras = s.options.filter((o) => !o.isCorrect);
    poner({
      prompt: s.prompt,
      options: [correcta, ...otras].map((o) => o?.text ?? ''),
      explanation: s.explanation,
      pivot: s.pivot,
      bibliography: s.bibliography,
    });
    setSugerencia(null);
    toast.info('Sugerencia aplicada al formulario. Revisa y guarda.');
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
    >
      {!esSubsecuente && (
        <>
          <Campo etiqueta="Especialidad">
            <select
              className="input-vidrio"
              disabled={solo}
              value={f.specialtyId ?? ''}
              onChange={(e) =>
                poner({ specialtyId: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">Sin especialidad</option>
              {esp.data?.map((e) => (
                <option key={e.id} value={e.id}>
                  {nombreEspecialidad(e)}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Caso clínico">
            <textarea
              className="input-vidrio min-h-40"
              disabled={solo}
              value={f.clinicalCase ?? ''}
              onChange={(e) => poner({ clinicalCase: e.target.value })}
            />
          </Campo>
        </>
      )}
      <Campo etiqueta="Pregunta">
        <textarea
          className="input-vidrio min-h-20"
          required
          disabled={solo}
          value={f.prompt}
          onChange={(e) => poner({ prompt: e.target.value })}
        />
      </Campo>
      <fieldset className="space-y-2">
        <legend className="mb-1 text-sm font-bold text-texto-2">
          Opciones (la A es la correcta; la plataforma las baraja)
        </legend>
        {f.options.map((o, i) => (
          <label key={i} className="flex items-start gap-2">
            <span
              className={`mt-2 grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-extrabold ${i === 0 ? 'bg-verde-500 text-white' : 'bg-[var(--pista)]'}`}
            >
              {LETRAS[i]}
            </span>
            <input
              className="input-vidrio"
              required
              disabled={solo}
              value={o}
              aria-label={`Opción ${LETRAS[i]}`}
              onChange={(e) =>
                poner({ options: f.options.map((x, j) => (j === i ? e.target.value : x)) })
              }
            />
          </label>
        ))}
      </fieldset>
      <Campo etiqueta="Justificación">
        <textarea
          className="input-vidrio min-h-28"
          disabled={solo}
          value={f.explanation ?? ''}
          onChange={(e) => poner({ explanation: e.target.value })}
        />
      </Campo>
      <div className="grid gap-3 sm:grid-cols-3">
        <Campo etiqueta="Pivote (dato clave)">
          <input
            className="input-vidrio"
            disabled={solo}
            value={f.pivot ?? ''}
            onChange={(e) => poner({ pivot: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Bibliografía">
          <input
            className="input-vidrio"
            disabled={solo}
            value={f.bibliography ?? ''}
            onChange={(e) => poner({ bibliography: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Código">
          <input
            className="input-vidrio"
            disabled={solo}
            value={f.code ?? ''}
            onChange={(e) => poner({ code: e.target.value })}
          />
        </Campo>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        {(
          [
            ['isOnline', 'Online'],
            ['isOnlineDemo', 'Online demo'],
            ['isQuiz', 'Quiz'],
            ['isQuizDemo', 'Quiz demo'],
            ['isEnglish', 'En inglés'],
          ] as const
        ).map(([k, t]) => (
          <label key={k} className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              disabled={solo}
              checked={f[k] ?? false}
              onChange={(e) => poner({ [k]: e.target.checked })}
            />
            {t}
          </label>
        ))}
      </div>
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          {actual && (
            <>
              <input
                className="input-vidrio max-w-xs"
                placeholder="Indicaciones para la IA (opcional)"
                value={indicaciones}
                onChange={(e) => setIndicaciones(e.target.value)}
              />
              <button
                type="button"
                className="btn-cs btn-contorno"
                disabled={sugerir.isPending}
                onClick={() => sugerir.mutate()}
              >
                <Sparkles size={16} aria-hidden />{' '}
                {sugerir.isPending ? 'Pensando…' : 'Sugerir mejora con IA'}
              </button>
            </>
          )}
        </div>
      )}
      <Modal
        abierto={sugerencia !== null}
        onCerrar={() => setSugerencia(null)}
        titulo="Sugerencia de la IA"
        ancho="max-w-2xl"
      >
        {sugerencia && (
          <div className="space-y-3 text-sm">
            <p className="font-bold">{sugerencia.prompt}</p>
            <ul className="space-y-1">
              {sugerencia.options.map((o, i) => (
                <li key={i} className={o.isCorrect ? 'font-bold text-verde-texto' : ''}>
                  {o.isCorrect ? '✔ ' : '• '}
                  {o.text} <span className="font-normal text-texto-3">— {o.rationale}</span>
                </li>
              ))}
            </ul>
            <p className="text-texto-2">{sugerencia.explanation}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-cs btn-contorno"
                onClick={() => setSugerencia(null)}
              >
                Descartar
              </button>
              <button
                type="button"
                className="btn-cs btn-primario"
                onClick={() => aplicar(sugerencia)}
              >
                Usar en el formulario
              </button>
            </div>
          </div>
        )}
      </Modal>
    </form>
  );
}

function Editor({ actual }: { actual: PreguntaDetalle | null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editable = useCan('preguntas', 'editar');
  const [nuevaSub, setNuevaSub] = useState(false);
  const [borrar, setBorrar] = useState(false);
  const ruta = { params: { path: { id: String(actual?.id) } } };
  const refrescar = (p: PreguntaDetalle) => {
    qc.setQueryData(['preguntas', p.id], p);
    void qc.invalidateQueries({ queryKey: ['preguntas', 'lista'] });
  };
  const subirImagen = useMutation({
    mutationFn: async (file: File) => {
      const err = validarImagen(file);
      if (err) throw new Error(err);
      return unwrap<PreguntaDetalle>(
        api.PUT('/api/v1/admin/questions/{id}/image', {
          ...ruta,
          body: { image: await leerBase64(file) },
        }),
      );
    },
    onSuccess: (p) => {
      refrescar(p);
      toast.success('Imagen actualizada.');
    },
    onError: (e) =>
      toast.error(e instanceof Error && e.name !== 'ApiError' ? e.message : mensajeDe(e)),
  });
  const quitarImagen = useMutation({
    mutationFn: () =>
      unwrap<PreguntaDetalle>(api.DELETE('/api/v1/admin/questions/{id}/image', ruta)),
    onSuccess: refrescar,
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const eliminar = useMutation({
    mutationFn: () => ensureOk(api.DELETE('/api/v1/admin/questions/{id}', ruta)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['preguntas', 'lista'] });
      toast.success('Caso eliminado.');
      void navigate('/preguntas', { replace: true });
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  return (
    <>
      <Link to="/preguntas" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold">
        <ArrowLeft size={16} aria-hidden /> Banco de casos
      </Link>
      <Encabezado
        titulo={actual ? `Caso #${actual.id}` : 'Nuevo caso'}
        descripcion={
          actual
            ? `${actual.specialtyName ?? 'Sin especialidad'} · origen ${actual.source ?? '—'}${actual.exams.length ? ` · en ${actual.exams.length} examen(es)` : ''}${actual.openReports ? ` · ${actual.openReports} reporte(s) abiertos` : ''}`
            : undefined
        }
        acciones={
          actual &&
          editable && (
            <button type="button" className="btn-cs btn-peligro" onClick={() => setBorrar(true)}>
              <Trash2 size={16} aria-hidden /> Eliminar
            </button>
          )
        }
      />
      <div className="grid gap-6 xl:grid-cols-[3fr_2fr]">
        <section className="panel-vidrio">
          <FormPregunta
            key={actual?.updatedAt ?? 'nueva'}
            actual={actual}
            baseId={null}
            onGuardada={(p) => {
              refrescar(p);
              if (!actual) void navigate(`/preguntas/${p.id}`, { replace: true });
            }}
          />
        </section>
        {actual && (
          <div className="space-y-6">
            <section className="panel-vidrio space-y-3">
              <h2 className="text-lg font-extrabold">Imagen</h2>
              {actual.imageUrl ? (
                <img src={actual.imageUrl} alt="Imagen del caso" className="max-h-72 rounded-xl" />
              ) : (
                <p className="text-sm text-texto-3">Sin imagen.</p>
              )}
              {editable && (
                <div className="flex gap-2">
                  <label className="btn-cs btn-contorno cursor-pointer">
                    <ImagePlus size={16} aria-hidden />{' '}
                    {subirImagen.isPending ? 'Subiendo…' : 'Subir imagen'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="sr-only"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) subirImagen.mutate(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {actual.imageUrl && (
                    <button
                      type="button"
                      className="btn-cs btn-contorno"
                      onClick={() => quitarImagen.mutate()}
                    >
                      Quitar
                    </button>
                  )}
                </div>
              )}
            </section>
            <section className="panel-vidrio space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold">Preguntas subsecuentes</h2>
                {editable && (
                  <button
                    type="button"
                    className="btn-cs btn-contorno"
                    onClick={() => setNuevaSub(true)}
                  >
                    <Plus size={16} aria-hidden /> Agregar
                  </button>
                )}
              </div>
              {actual.children.length === 0 && <p className="text-sm text-texto-3">Ninguna.</p>}
              <ul className="space-y-2">
                {actual.children.map((c) => (
                  <li key={c.id}>
                    <Link to={`/preguntas/${c.id}`} className="font-semibold">
                      #{c.id} · {c.prompt.slice(0, 90)}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
            {actual.exams.length > 0 && (
              <section className="panel-vidrio space-y-2">
                <h2 className="text-lg font-extrabold">En exámenes</h2>
                <ul className="text-sm">
                  {actual.exams.map((e) => (
                    <li key={e.id}>
                      <Link to={`/preguntas/examenes/${e.id}`}>{e.name}</Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {actual.contentLinks.length > 0 && (
              <section className="panel-vidrio space-y-2">
                <h2 className="text-lg font-extrabold">Ligado a contenido</h2>
                <ul className="text-sm">
                  {actual.contentLinks.map((l) => (
                    <li key={l.id}>
                      {l.contentType ?? 'contenido'} ·{' '}
                      {l.libraryItemName ?? `#${l.libraryItemId ?? '—'}`}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
      {actual && (
        <Modal
          abierto={nuevaSub}
          onCerrar={() => setNuevaSub(false)}
          titulo="Nueva pregunta subsecuente"
          ancho="max-w-2xl"
        >
          <FormPregunta
            actual={null}
            baseId={actual.id}
            onGuardada={() => {
              setNuevaSub(false);
              void qc.invalidateQueries({ queryKey: ['preguntas', actual.id] });
            }}
          />
        </Modal>
      )}
      <ConfirmarAccion
        abierto={borrar}
        onCerrar={() => setBorrar(false)}
        titulo="Eliminar caso"
        descripcion="Se borran el caso y sus subsecuentes. No se puede si está en un examen o ya tiene respuestas de alumnos."
        textoBoton="Eliminar"
        peligro
        ocupado={eliminar.isPending}
        onConfirmar={() => eliminar.mutate()}
      />
    </>
  );
}

export function PreguntaPage() {
  const { id } = useParams();
  const numero = id && id !== 'nueva' ? Number(id) : null;
  const q = usePregunta(numero);
  return (
    <PermissionGate seccion="preguntas" nivel={numero === null ? 'editar' : 'ver'}>
      {numero === null ? (
        <Editor actual={null} />
      ) : q.isPending ? (
        <Cargando />
      ) : q.isError ? (
        <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />
      ) : (
        <Editor key={q.data.id} actual={q.data} />
      )}
    </PermissionGate>
  );
}
